/**
 * THE TWO RENDERS THAT KEEP AN EMPTY LIST HONEST.
 *
 * A list screen has three states and they are not interchangeable:
 *
 *   still loading   -> <ListSkeleton />   "we are asking"
 *   failed to load  -> <LoadError />      "we asked and it went wrong; try again"
 *   genuinely empty -> the screen's own empty state, with its own call to action
 *
 * Six screens in this product rendered the third one for all three, because the
 * fetch had no error branch and no loading flag: an owner with forty buildings
 * was told "No properties yet", and Account › Devices told a reader "No other
 * active sessions." while the request was failing. Pairing this with
 * `useResource` is what makes the wrong render hard to write.
 *
 * The visual language is the one the Portfolios screen already established —
 * the coral banner with the round alert glyph, and the shimmering rows — lifted
 * here rather than copied a third time.
 *
 * MOTION. None of these carry an entrance of their own any more: every screen
 * mounts them inside a `StateSwitch` (src/motion), which crossfades the four
 * fills of one box — skeleton, error, empty, content — so the banner arrives
 * the same way the table it replaces does. The shimmer is the one ambient loop
 * here, and it reads its speed from the shared `LOOP` so the app has one.
 */
import React from 'react';
import styled from '@emotion/styled';
import {
  layout,
  palette,
  radius,
  shadow,
  spacing,
  status,
  transition,
  typography,
} from '../../../../style-utils/theme';
import { shimmerLoop } from '../../../../style-utils/motion';

const Banner = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: ${spacing.xs};
  width: 100%;
  padding: ${spacing.sm};
  margin-bottom: ${spacing.xs};
  border: 1px solid ${palette.alert};
  border-radius: ${radius.sm};
  background: ${palette.alertSoft};
  /* status.alert.fg, not palette.alert: this is TEXT on a tint, and #F0564A on
     #FCE9E7 measures 1.9:1. The triple in theme.ts is the pairing that passes. */
  color: ${status.alert.fg};

  & > svg {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    margin-top: ${spacing.half};
  }
`;

const Message = styled.p`
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${status.alert.fg};
  word-break: break-word;
`;

const Retry = styled.button`
  -webkit-appearance: none;
  appearance: none;
  flex: 0 0 auto;
  min-height: ${layout.buttonHeight};
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.alertStrong};
  border-radius: ${radius.xs};
  background: transparent;
  color: ${status.alert.fg};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    background: ${palette.alertStrong};
    color: ${palette.white};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focusError};
  }
`;

/* The gap between skeleton rows. Exported through `skeletonHeight` so a screen
   can reserve exactly the box the skeleton will occupy and the page never
   jumps when the content lands. */
const ROW_GAP = 8;

const Row = styled.div<{ $height: number }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  width: 100%;
  min-height: ${({ $height }) => $height}px;
  padding: ${spacing.sm};
  margin-bottom: ${ROW_GAP}px;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};
`;

const Line = styled.div<{ w?: string; h?: number }>`
  height: ${({ h }) => h || 12}px;
  width: ${({ w }) => w || '100%'};
  border-radius: ${radius.xs};
  background: linear-gradient(
    90deg,
    ${palette.surfaceMuted} 0%,
    ${palette.line} 50%,
    ${palette.surfaceMuted} 100%
  );
  /* The shared keyframe sweeps in pixels (-480 → 480), so the gradient tile
     has to be wider than the sweep or the seam shows. */
  background-size: 960px 100%;
  ${shimmerLoop};
`;

export interface LoadErrorProps {
  /** What went wrong, in the reader's language. */
  message: string;
  /** Run the same load again. */
  onRetry: () => void;
  /** Override when "Try again" is not the right verb. */
  retryLabel?: string;
}

/**
 * role="alert" so the failure is ANNOUNCED, not merely drawn. A screen reader
 * user gets no other signal that the list they asked for is not coming.
 */
export const LoadError: React.FC<LoadErrorProps> = ({
  message,
  onRetry,
  retryLabel = 'Try again',
}) => (
  <Banner role="alert">
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4.5" />
      <path d="M12 16h.01" />
    </svg>
    <Message>{message}</Message>
    <Retry type="button" onClick={onRetry}>
      {retryLabel}
    </Retry>
  </Banner>
);

export interface ListSkeletonProps {
  /** How many placeholder rows. Match the usual page, not the maximum. */
  rows?: number;
  /** What is being fetched, for assistive tech: "buildings", "users". */
  label?: string;
  /**
   * The height of one REAL row on the screen this stands in for, so the
   * skeleton occupies the box the content will and nothing moves when the
   * rows land. Default is the 64px card row.
   */
  rowHeight?: number;
}

/** The box `rows` skeleton rows occupy — pass to StateSwitch's minHeight. */
export const skeletonHeight = (rows: number, rowHeight = 64): number =>
  rows * (rowHeight + ROW_GAP);

export const ListSkeleton: React.FC<ListSkeletonProps> = ({
  rows = 4,
  label = 'content',
  rowHeight = 64,
}) => (
  <div role="status" aria-busy="true" aria-label={`Loading ${label}`}>
    {Array.from({ length: rows }, (_, index) => (
      <Row key={index} $height={rowHeight} aria-hidden="true">
        <Line w="40%" />
        <Line w="20%" />
        <Line w="15%" />
      </Row>
    ))}
  </div>
);

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin-top: ${spacing.sm};
`;

export interface FormSkeletonProps {
  /** How many label + field pairs. Match the form it stands in for. */
  fields?: number;
  label?: string;
}

/** The height `fields` skeleton fields occupy — pass to StateSwitch's minHeight. */
export const formSkeletonHeight = (fields: number): number =>
  fields * (48 + 12 + 8 + 16);

/**
 * A form-shaped placeholder: a short label line over a field-height block,
 * repeated. For the moments a FORM is what is coming — the building modal
 * opened from a deep link before its list has answered.
 */
export const FormSkeleton: React.FC<FormSkeletonProps> = ({
  fields = 4,
  label = 'form',
}) => (
  <div role="status" aria-busy="true" aria-label={`Loading ${label}`}>
    {Array.from({ length: fields }, (_, index) => (
      <FieldBlock key={index} aria-hidden="true">
        <Line w="30%" />
        <Line w="100%" h={48} />
      </FieldBlock>
    ))}
  </div>
);
